import { useNavigate } from 'react-router-dom';
import { Alert, Box, Stack } from '@mui/material';

import { HasPermission } from '@components/auth';
import { PageContentTemplate, PageContentTemplateProps, SelectableProps } from '@components/base';
import useLayoutEntityFilterContext from '@hooks/useLayoutEntityFilter';
import LayoutEntity from '@models/LayoutEntity';
import LayoutEntityTable, {
  LayoutEntityTableProps,
} from '@modules/layouts/components/LayoutEntityTable';

import QuickAddTagFilter from './QuickAddTagFilter';

type LayoutEntityListPageProps<T extends LayoutEntity> = SelectableProps &
  Pick<
    LayoutEntityTableProps<T>,
    'columns' | 'contentType' | 'entityRoute' | 'hasOptions' | 'TableRowComponent'
  > & {
    hiddenItemIds?: string[];
    PageContentWrapperProps: Omit<PageContentTemplateProps, 'children' | 'contentType'>;
    typeField?: string;
  };

function LayoutEntityListPage<T extends LayoutEntity>({
  columns,
  contentType,
  entityRoute,
  hiddenItemIds = [],
  PageContentWrapperProps: { addNewButton, ...pageContentWrapperProps },
  selectable,
  ...rest
}: LayoutEntityListPageProps<T>) {
  const navigate = useNavigate();

  const { error, favoriteTags, filteredEntities, loading, hasFilters, tagValues, queryText } =
    useLayoutEntityFilterContext<T>();

  return (
    <PageContentTemplate
      addNewButton={
        <Stack alignItems='flex-end' direction='row' spacing={1} width={1}>
          <HasPermission>{addNewButton}</HasPermission>

          {tagValues && favoriteTags && (
            <Box flex={1}>
              <QuickAddTagFilter favoriteTags={favoriteTags} />
            </Box>
          )}
        </Stack>
      }
      ContentBoxProps={{ flex: 1, position: 'relative' }}
      contentType={contentType}
      WrapperProps={{ height: 1, overflow: 'hidden' }}
      {...pageContentWrapperProps}
    >
      {error ? (
        <Alert severity='error'>{error.message}</Alert>
      ) : (
        <LayoutEntityTable
          columns={columns}
          contentType={contentType}
          emptyStateContext={hasFilters || queryText ? 'search' : undefined}
          entityRoute={entityRoute}
          getTableRowProps={
            entityRoute ? entity => ({ onClick: () => navigate(entityRoute(entity)) }) : undefined
          }
          hasTags={Boolean(tagValues)}
          idKey='id'
          items={filteredEntities.filter(({ id }) => !hiddenItemIds.includes(id))}
          loading={loading}
          selectable={selectable}
          {...rest}
        />
      )}
    </PageContentTemplate>
  );
}

export default LayoutEntityListPage;
